import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { FilialResumo } from '@/lib/types';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'agilizaos_filial_id';

type FilialContextValue = {
  filialId: string;
  setFilialId: (id: string) => void;
  filiais: FilialResumo[];
  /** Carregamento inicial ou refetch sem dados em cache */
  isLoading: boolean;
  isError: boolean;
  refetchFiliais: () => void;
};

const FilialContext = createContext<FilialContextValue | null>(null);

export function FilialProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [filialIdState, setFilialIdState] = useState(() => localStorage.getItem(STORAGE_KEY) ?? '');

  const filiaisQuery = useQuery({
    queryKey: ['filiais'],
    queryFn: () => api.get<FilialResumo[]>('/filiais').then((r) => r.data),
    enabled: isAuthenticated,
  });
  const filiais = filiaisQuery.data ?? [];
  const isLoading = filiaisQuery.isLoading && !filiaisQuery.isError;

  /** Sempre um id válido quando já há filiais (evita Select Radix uncontrolled → controlled). */
  const filialId = useMemo(() => {
    if (!filiais.length) return '';
    if (filialIdState && filiais.some((f) => f.id === filialIdState)) return filialIdState;
    if (user?.filialId && filiais.some((f) => f.id === user.filialId)) return user.filialId;
    return filiais[0].id;
  }, [filiais, filialIdState, user?.filialId]);

  useEffect(() => {
    if (!filialId || filialId === filialIdState) return;
    setFilialIdState(filialId);
    localStorage.setItem(STORAGE_KEY, filialId);
  }, [filialId, filialIdState]);

  const setFilialId = useCallback((id: string) => {
    setFilialIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const value = useMemo(
    () => ({
      filialId,
      setFilialId,
      filiais,
      isLoading,
      isError: filiaisQuery.isError,
      refetchFiliais: filiaisQuery.refetch,
    }),
    [filialId, setFilialId, filiais, isLoading, filiaisQuery.isError, filiaisQuery.refetch],
  );

  return <FilialContext.Provider value={value}>{children}</FilialContext.Provider>;
}

export function useFilial() {
  const ctx = useContext(FilialContext);
  if (!ctx) {
    throw new Error('useFilial deve ser usado dentro de FilialProvider');
  }
  return ctx;
}
